import React, {useState, useEffect, useContext} from "react";
import {css} from "emotion";
import {colors, boxStyles, textSizes} from "../../styles/theme";
import copy from "copy-to-clipboard";
import Confetti from "react-dom-confetti";
import GenericButton from "../../components/GenericButton";
import {GlobalContext} from "../../context/GlobalState";
import workspaces from "../../services/api/workspaces";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {darken} from "polished";

const EmbedBody = () => {
  const [scriptWasCopied, setScriptWasCopied] = useState(false);
  const [idWasCopied, setIdWasCopied] = useState(false);
  const [embedCode, setEmbedCode] = useState("");
  const {currentWorkspace} = useContext(GlobalContext);
  let _embed;
  let _workspaceId;

  const copyElement = (copyTarget) => {
    if (copyTarget === _embed) {
      const stringToCopy = copyTarget.childNodes[0].nodeValue;
      copy(stringToCopy);
      setScriptWasCopied(true);
      return;
    } else if (copyTarget === _workspaceId) {
      const stringToCopy = copyTarget.value;
      copy(stringToCopy);
      setIdWasCopied(true);
      return;
    }
  };

  const handleInputClick = (e) => {
    const tag = e.target.tagName;
    if (tag === "INPUT") {
      e.target.select();
    } else {
      if (document.selection) {
        // IE
        let range = document.body.createTextRange();
        range.moveToElementText(e.target);
        range.select();
      } else if (window.getSelection) {
        let range = document.createRange();
        range.selectNode(e.target);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }
    }

    copyElement(e.target);
  };

  const doNetworkRequest = async () => {
    try {
      const {data} = await workspaces.getEmbedCode(currentWorkspace);
      setEmbedCode(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    doNetworkRequest();
  }, []);

  return (
    <div className={container}>
      <div className="step">
        <div className="header">STEP 1</div>
        <div className="stepName">Copy Code</div>
        <div className="embedCode">
          <pre
            id="toCopy"
            ref={(element) => (_embed = element)}
            onClick={handleInputClick}
          >
            {embedCode
              .replace(/^ {4}/gm, "")
              .replace(/^ {2}<!--/gm, "<!--")
              .trim() ||
            `<!-- start Spotlight code -->
  <script>
    window.spotlightSettings = {
      workspaceId: %WORKSPACE_ID%,
    }
  </script>
  <script>
    !function(){function t(){var t,e,n,a=document.createElement("script"),o=document.getElementsByTagName("script")[0];t="https://spotlight-api.herokuapp.com/v1/widget/version",e=function(t){var e=t;a.type="text/javascript",a.async=!0,a.src="https://cdn.jsdelivr.net/npm/@trolley/spotlight@"+e+"/spotlight.min.js",o.parentNode.insertBefore(a,o)},(n=new XMLHttpRequest).onreadystatechange=function(){4===n.readyState&&200===n.status&&e&&e(n.responseText)},n.open("GET",t,!0),n.send(null)}window.attachEvent?window.attachEvent("onload",t):window.addEventListener("load",t,!1)}();
  </script>
<!-- end Spotlight code -->`}
          </pre>
          <GenericButton
            text={scriptWasCopied ? "copied!" : "copy"}
            handleClick={() => copyElement(_embed)}
            style="inputStyle"
          />
          <Confetti
            active={scriptWasCopied}
            config={{
              colors: [
                "#4996e9",
                "#1E3290",
                "#4a154b",
                "#bc5090",
                "#ff6361",
                "#ffa600",
                colors.accentColor,
              ],
              elementCount: 50,
              spread: 90,
              startVelocity: 25,
              height: "7px",
              width: "7px",
            }}
          />
        </div>
      </div>
      <div className="step">
        <div className="header">STEP 2</div>
        <div className="stepName">Paste In Head Element</div>
        <div className="instruction">
          Paste the code right after the opening &lt;head&gt; tag or right
          before the closing &lt;/head&gt; tag.
        </div>
      </div>
      <div className="step">
        <div className="header">STEP 3</div>
        <div className="stepName">Refresh Website</div>
        <div className="instruction">
          Once refreshed, the website should show your new Spotlight. You can
          now edit your spotlight from the home tab.
        </div>
      </div>
      <div className="break"/>
      <div className="step">
        <div className="header">WORDPRESS INSTALLATION</div>

        <div className="instruction">
          <div className="topRow">
            <a href="/downloads/spotlightwp.zip" target="_blank">
              <FontAwesomeIcon className="icon" icon="cloud-download-alt"/>
              <span>Download the zip file</span>
            </a>
          </div>
          <div className="bottomRow">
            <div className="workspaceHeader">Your Workspace ID:</div>
            <div className="copyRow">
              <input
                readOnly={true}
                value={currentWorkspace.id}
                ref={(element) => (_workspaceId = element)}
                onClick={handleInputClick}
              />

              <GenericButton
                text={idWasCopied ? "copied!" : "copy"}
                handleClick={() => copyElement(_workspaceId)}
                style="idButtonStyle"
              />
              <Confetti
                active={idWasCopied}
                config={{
                  colors: [
                    "#4996e9",
                    "#1E3290",
                    "#4a154b",
                    "#bc5090",
                    "#ff6361",
                    "#ffa600",
                    colors.accentColor,
                  ],
                  elementCount: 50,
                  spread: 90,
                  startVelocity: 25,
                  height: "7px",
                  width: "7px",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmbedBody;

const container = css`
  padding: ${boxStyles.standardPadding};
  
  @media screen and (min-width: ${boxStyles.desktopBreak}) {
    width: 60%;
  }
  
  .break {
    background-color: ${colors.lineBreak};
    height: 1px;
    margin-top: 10px;
  }

  .step {
    padding: 20px 0;
  }

  .header {
    color: ${colors.placeholderText};
    font-size: ${textSizes.normal};
    font-weight: bold;
  }
  .stepName {
    font-size: ${textSizes.subtitle};
    font-weight: bold;
    margin-bottom: 8px;
  }

  .topRow {
    font-size: ${textSizes.subtitle};
    margin-top: 10px;

    a {
      color: ${colors.brandColor};
      text-decoration: none;
      
      .icon {
        font-size: 16px;
      }
      
        &:hover {
          cursor: pointer;
          color: ${darken(0.1, colors.brandColor)};
          text-decoration: underline;
        }
      }

      span {
        padding: 0 10px;
      }
    }
  }

  .bottomRow {
    display: flex;
    flex-direction: column;

    .workspaceHeader {
      font-size: ${textSizes.normal};
      padding-top: 20px;
      margin-bottom: 5px;
      font-weight: 500;
    }

    .copyRow {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;

      input {
        color: ${colors.inputTextColor};
        box-sizing: border-box;
        width: 100%;
        background-color: ${colors.inputBackground};
        padding: 10px;
        border: none;
        border-radius: 8px 0 0 8px;
        color: ${colors.inputTextColor};
        font-size: ${textSizes.intermediate};
        outline: none;
        max-width: 240px;
      }

      .idButtonStyle {
        color: white;
        outline: none;
        padding: 4px 12px;
        font-size: 12px;
        letter-spacing: 1px;
        width: auto;
        border-radius: 0px 8px 8px 0;

        p {
          padding: 2px 0 0 0;
          font-size: ${textSizes.intermediate};
          font-weight: normal;
        }
      }
    }
  }

  #toCopy {
    font-size: ${textSizes.normal};
    font-family: monospace;
    border: solid;
    border-radius: 5px;
    border-color: #4996e9;
    outline: none;
    height: 160px;
    width: 100%;
    box-sizing: border-box;
    padding: 8px;
    -webkit-box-shadow: none;
    -moz-box-shadow: none;
    box-shadow: none;
    resize: none;
    overflow-x: scroll;

    @media screen and (min-width: ${boxStyles.desktopBreak}) {
      height: 160px;
      width: 100%;
    }
  }

  .instruction {
    font-size: ${textSizes.intermediate};
    color: ${colors.subtitleColor};
  }

  .embedCode {
    position: relative;

    div {
      position: absolute;
      right: 40px;
      bottom: 0;
    }
  }

  .inputStyle {
    color: white;
    position: absolute;
    bottom: 15px;
    right: 20px;
    outline: none;
    padding: 4px 12px;
    font-size: 12px;
    letter-spacing: 1px;
    width: auto;

    p {
      padding: 2px 0 0 0;
      font-size: 10px;
      font-weight: normal;
    }

    @media screen and (min-width: ${boxStyles.desktopBreak}) {
      bottom: 15px;
      right: 20px;
    }
  }
`;
